import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { EyeIcon, EyeSlashIcon, SunIcon, MoonIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import Logo from '../components/icons/Logo';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

interface LoginFormData {
  email: string;
  password: string;
  displayName?: string;
}

const Login = () => {
  const { user, signInWithGoogle, signInWithEmail, signUpWithEmail, resetPassword } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const [authMode, setAuthMode] = useState<'login' | 'register' | 'reset'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm<LoginFormData>();

  if (user) {
    // Customer szerepkör az ügyfélportálra irányít
    if (user.role === 'customer') {
      return <Navigate to="/customer-portal" replace />;
    }
    return <Navigate to="/" replace />;
  }

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    
    try {
      if (authMode === 'login') {
        await signInWithEmail(data.email, data.password);
      } else if (authMode === 'register') {
        await signUpWithEmail(data.email, data.password, data.displayName);
      } else if (authMode === 'reset') {
        await resetPassword(data.email);
        setAuthMode('login');
        reset();
      }
    } catch (error) {
      console.error('Auth error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModeChange = (newMode: 'login' | 'register' | 'reset') => {
    setAuthMode(newMode);
    reset();
    setShowPassword(false);
  };

  const getTitle = () => {
    switch (authMode) {
      case 'login': return t('auth.login') || 'Bejelentkezés';
      case 'register': return t('auth.register') || 'Regisztráció';
      case 'reset': return t('auth.resetPassword') || 'Jelszó visszaállítása';
    }
  };

  const getSubtitle = () => {
    switch (authMode) {
      case 'login': return t('auth.loginSubtitle') || 'Jelentkezz be az ügyfeleid kezeléséhez';
      case 'register': return t('auth.registerSubtitle') || 'Hozz létre egy új fiókot';
      case 'reset': return t('auth.resetSubtitle') || 'Add meg az e-mail címed a jelszó visszaállításához';
    }
  };

  return (
    <div className="min-h-screen relative bg-gray-100 dark:bg-gray-900 flex items-center justify-center overflow-hidden">
      {/* Theme and Language Selectors */}
      <div className="absolute top-4 right-4 z-20 flex items-center space-x-3">
        {/* Theme Selector */}
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm hover:bg-white/90 dark:hover:bg-gray-800/90 transition-all duration-200 shadow-md"
          aria-label={theme === 'light' ? 'Váltás sötét módra' : 'Váltás világos módra'}
        >
          {theme === 'light' ? (
            <MoonIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          ) : (
            <SunIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          )}
        </button>

        {/* Language Selector */}
        <button
          onClick={() => setLanguage(language === 'hu' ? 'en' : 'hu')}
          className="p-2 rounded-lg bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm hover:bg-white/90 dark:hover:bg-gray-800/90 transition-all duration-200 shadow-md flex items-center space-x-1"
          aria-label={language === 'hu' ? 'Switch to English' : 'Váltás magyarra'}
        >
          <GlobeAltIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 uppercase">
            {language === 'hu' ? 'EN' : 'HU'}
          </span>
        </button>
      </div>

      {/* Professional minimal background */}
      <div className="absolute inset-0">
        {/* Clean gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-100 via-amber-50 to-blue-100 dark:from-black dark:via-indigo-950 dark:to-blue-950 animate-gradient-slow">
          {/* Overlay gradient layer */}
          <div className="absolute inset-0 bg-gradient-to-tl from-purple-100/60 via-orange-100/40 to-indigo-100/60 dark:from-purple-950/60 dark:via-orange-950/50 dark:to-indigo-950/60 animate-gradient-slower" />
          {/* Third gradient layer for more complexity */}
          <div className="absolute inset-0 bg-gradient-to-tr from-yellow-100/30 via-transparent to-blue-100/40 dark:from-yellow-600/30 dark:via-transparent dark:to-blue-950/40 animate-gradient-medium" />
          {/* Subtle texture overlay */}
          <div className="absolute inset-0 opacity-[0.015] dark:opacity-[0.03]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Ccircle cx='7' cy='7' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
      </div>

      {/* Add custom animations */}
      <style>{`
        @keyframes gradient-slow {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes gradient-slower {
          0%, 100% { background-position: 100% 50%; }
          50% { background-position: 0% 50%; }
        }
        @keyframes gradient-medium {
          0%, 100% { background-position: 50% 0%; }
          50% { background-position: 50% 100%; }
        }
        .animate-gradient-slow {
          background-size: 200% 200%;
          animation: gradient-slow 15s ease infinite;
        }
        .animate-gradient-slower {
          background-size: 200% 200%;
          animation: gradient-slower 20s ease infinite;
        }
        .animate-gradient-medium {
          background-size: 200% 200%;
          animation: gradient-medium 12s ease infinite;
        }
      `}</style>

      {/* Login container */}
      <div className="relative max-w-md w-full m-4">
        <div className="bg-white/98 dark:bg-gray-900/95 backdrop-blur-md p-8 rounded-2xl shadow-2xl border border-white/20 dark:border-indigo-500/30 ring-1 ring-black/5 dark:ring-indigo-400/20">
          <div className="text-center mb-8">
            <Logo className="h-16 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{getTitle()}</h1>
            <p className="text-gray-600 dark:text-gray-400">{getSubtitle()}</p>
          </div>
          {/* Email/Password Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mb-6">
            {/* Display Name - only for register */}
            {authMode === 'register' && (
              <div>
                <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('auth.displayName') || 'Név'}
                </label>
                <input
                  type="text"
                  id="displayName"
                  {...register('displayName', {
                    required: authMode === 'register' ? (t('auth.displayNameRequired') || 'A név megadása kötelező') : false
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Teljes név"
                />
                {errors.displayName && (
                  <p className="mt-1 text-sm text-red-600">{errors.displayName.message}</p>
                )}
              </div>
            )}

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('auth.email') || 'E-mail cím'}
              </label>
              <input
                type="email"
                id="email"
                {...register('email', {
                  required: t('auth.emailRequired') || 'Az e-mail cím megadása kötelező',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('auth.invalidEmail') || 'Érvénytelen e-mail formátum'
                  }
                })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-700 dark:text-gray-100"
                placeholder="pelda@email.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            {/* Password - not for reset */}
            {authMode !== 'reset' && (
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('auth.password') || 'Jelszó'}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    {...register('password', {
                      required: t('auth.passwordRequired') || 'A jelszó megadása kötelező',
                      minLength: {
                        value: 6,
                        message: t('auth.passwordTooShort') || 'A jelszó legalább 6 karakter hosszú legyen'
                      }
                    })}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-700 dark:text-gray-100"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-gray-800 hover:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-800 transform transition duration-200 ease-in-out hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {t('auth.processing') || 'Feldolgozás...'}
                </div>
              ) : (
                getTitle()
              )}
            </button>
          </form>

          {/* Forgot Password Link & Register Link */}
          {authMode === 'login' && (
            <div className="text-center mb-4 space-y-2">
              <button
                type="button"
                onClick={() => handleModeChange('reset')}
                className="block w-full text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {t('auth.forgotPassword') || 'Elfelejtetted a jelszavadat?'}
              </button>
              <button
                type="button"
                onClick={() => handleModeChange('register')}
                className="block w-full text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {t('auth.noAccount') || 'Nincs még fiókod? Regisztrálj!'}
              </button>
            </div>
          )}

          {/* Back to Login from Reset or Register */}
          {(authMode === 'reset' || authMode === 'register') && (
            <div className="text-center mb-4">
              <button
                type="button"
                onClick={() => handleModeChange('login')}
                className="text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {t('auth.backToLogin') || '← Vissza a bejelentkezéshez'}
              </button>
            </div>
          )}

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-600" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                {t('auth.or') || 'vagy'}
              </span>
            </div>
          </div>

          {/* Google Sign In Button */}
          <button
            onClick={signInWithGoogle}
            disabled={isLoading}
            className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 dark:border-gray-600 text-base font-medium rounded-xl text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transform transition duration-200 ease-in-out hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none mb-6"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {t('auth.signInWithGoogle') || 'Bejelentkezés Google-fiókkal'}
          </button>

        </div>
      </div>
    </div>
  );
};

export default Login; 